import { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { sleep } from '@/utils/sleep'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getTransactionTradingAccountAdjustmentFilters } from 'components/Filter/getTransactionTradingAccountAdjustmentFilters'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  setGridFilters,
} from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import { normalizeTransactions, normalizeTransactionsSortQuery } from './normalizer'
import TransactionReversePage from './TransactionReverse/TransactionReversePage'
import { transactionTradingAccountAdjustmentsColumns } from './transactionTradingAccountAdjustmentsColumns'

const useMapStateToProps = () => ({
  transactionStatus: useAppSelector((state) => state.transactions.status),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  gridState: useAppSelector((state) => state.grid),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    fetchTransactionTypes: async () =>
      dispatch(fetchTransactionTypeAction(walletsApiClient, { tags: 'TradingAccountAdjustments' })),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const TransactionTradingAccountAdjustmentsOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const walletsApiClient = useApiClient(WalletsApiClient)
  const permissions = auth?.permissions || []

  const [transactions, setTransactions] = useState<TransactionDto[]>([])
  const [reverseTransactionModal, setReverseTransactionModal] = useState<{
    data?: TransactionDto
    visible: boolean
  }>({ data: undefined, visible: false })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { transactionStatus, tickmillCompanies } = useMapStateToProps()
  const { fetchTransactionStatus, fetchTransactionTypes, fetchTickmillCompanies } =
    useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<TransactionDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          await fetchTransactionTypes()
          if (isEmpty(transactionStatus)) {
            await fetchTransactionStatus()
          }
          if (isEmpty(tickmillCompanies)) {
            await fetchTickmillCompanies()
          }

          if (response?.items) {
            setTransactions(normalizeTransactions(response.items))
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return buildQuery(gridFilter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery) => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(normalizeTransactions(response.items))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }
  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleGridPageChange = async (page: number) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getTradingAccountAdjustmentTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModal({ data: transaction, visible: true })
  }

  const handleReverseCompleteTransactionClose = async () => {
    setReverseTransactionModal({ data: undefined, visible: false })
    await sleep(1000)
    await filterFetch()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {reverseTransactionModal.visible && !!reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={handleReverseCompleteTransactionClose}
        >
          <TransactionReversePage
            transaction={reverseTransactionModal.data}
            onClose={handleReverseCompleteTransactionClose}
          />
        </Modal>
      )}
      <Breadcrumbs
        crumbs={[
          {
            title: 'Transactions - Trading Account Adjustments',
            link: links.transactionTradingAccountAdjustments.url,
          },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.TradingAccountAdjustmentsOverview}
        filterFields={[...getTransactionTradingAccountAdjustmentFilters()]}
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {transactionTradingAccountAdjustmentsColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={60}
              title=''
              locked
              cell={(gridProps: GridCellProps) => {
                if (isTransactionCompletedStatus(gridProps.dataItem.state.id)) {
                  return (
                    <td className={gridProps.className} style={gridProps.style}>
                      <MoreButton
                        items={[
                          {
                            name: 'Reverse',
                            onClick: () => handleReverseTransaction(gridProps.dataItem),
                          },
                        ]}
                      />
                    </td>
                  )
                }

                return <td className={gridProps.className} style={gridProps.style} />
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default TransactionTradingAccountAdjustmentsOverview
